/**
 * Merko Integrations — deployment gate.
 *
 * The handoff's first instruction is a gate, not a feature: the catalog and its
 * REST routes are added to the API only when the backend CDK context value
 * `integrations_enabled` is Boolean `true`. In the planned disabled deployment
 * the routes do **not** exist, so calling them 404s.
 *
 * `REACT_APP_INTEGRATIONS_ENABLED` is this app's own switch for that. It keeps
 * the existing Integration tab intact until the backend routes are actually
 * deployed — the handoff allows either keeping the old tab or feature-gating
 * the new UI, and gating is the reversible one.
 *
 * It is a *gate*, never a readiness signal. Even with routes deployed, each app
 * stays unavailable until its own deployment config is complete, and writes
 * need a further per-app flag. Readiness is only ever read from the catalog.
 */

const flag = (value: string | undefined): boolean => (value || "").trim().toLowerCase() === "true";

/** True when this environment's integrations routes are expected to exist. */
export const INTEGRATIONS_ENABLED: boolean = flag(process.env.REACT_APP_INTEGRATIONS_ENABLED);

/**
 * Path of the trusted Merko page that completes a connection attempt.
 *
 * Must be a page inside this app: it has to authenticate the *same* signed-in
 * Merko user and compare the callback's `attempt_id` against the stored one
 * before completing. A provider redirect landing anywhere else is not proof of
 * anything.
 */
const DEFAULT_CALLBACK_PATH = "/integrations/callback";

/**
 * Normalised so the value can be mounted as a route directly.
 *
 * A path that disagreed with the route the app actually mounts is worse than
 * no setting at all: the provider would redirect to a page that does not
 * exist, and the failure only shows up after a deploy. So this is the single
 * source for both, and anything that cannot be a route path falls back to the
 * default rather than producing one that cannot match.
 */
function routePath(raw: string | undefined): string {
    const value = (raw || "").trim();
    if (!value) return DEFAULT_CALLBACK_PATH;
    // A full URL, a query string or a fragment cannot be a route path.
    const path = value.split(/[?#]/)[0].trim();
    if (!path || /^[a-z][a-z0-9+.-]*:/i.test(path)) return DEFAULT_CALLBACK_PATH;
    const withSlash = path.startsWith("/") ? path : `/${path}`;
    // Trailing slashes would not match the redirect the provider was given.
    const trimmed = withSlash.replace(/\/+$/, "");
    return trimmed || DEFAULT_CALLBACK_PATH;
}

export const INTEGRATIONS_CALLBACK_PATH: string = routePath(
    process.env.REACT_APP_INTEGRATIONS_CALLBACK_PATH,
);

/** Exposed for the tests that hold the setting and the mounted route together. */
export const __routePathForTest = routePath;

/** Absolute callback URL for this origin. */
export function callbackUrl(): string {
    if (typeof window === "undefined") return INTEGRATIONS_CALLBACK_PATH;
    return new URL(INTEGRATIONS_CALLBACK_PATH, window.location.origin).toString();
}

/**
 * Suggested job-poll interval, per the handoff: 3–5 seconds, then back off.
 *
 * Explicitly "a frontend recommendation, not an API guarantee", so these are
 * local constants rather than anything negotiated with the server.
 */
export const JOB_POLL = {
    initialMs: 3_000,
    maxMs: 30_000,
    /** Multiplier applied once the job has been running a while. */
    backoffFactor: 1.5,
    /** Polls at the initial interval before backoff begins. */
    stablePolls: 10,
} as const;

/** Action polling uses the same shape; a write is short-lived either way. */
export const ACTION_POLL = {
    initialMs: 2_000,
    maxMs: 15_000,
    backoffFactor: 1.5,
    stablePolls: 10,
} as const;

/** Grows a poll interval once a job has been running for a while. */
export function pollInterval(
    attempt: number,
    tuning: { initialMs: number; maxMs: number; backoffFactor: number; stablePolls: number },
): number {
    if (attempt <= tuning.stablePolls) return tuning.initialMs;
    const grown = tuning.initialMs * tuning.backoffFactor ** (attempt - tuning.stablePolls);
    return Math.min(Math.round(grown), tuning.maxMs);
}
